/**
 * Offline reading (mobile M5): the last good answer for each read screen,
 * kept on the device, shown — clearly dated — when there is no signal.
 *
 * Only in the installed apps (the Android app and the iPhone home-screen
 * app), by the owner's answer to plan question 4. A desktop browser keeps no
 * copy and behaves exactly as before. In the Android app the copy sits in the
 * app's private storage, excluded from backup (M2), behind the fingerprint
 * lock (M4).
 *
 * The rules:
 *
 *  * **Never shown as if live.** Serving a saved answer flips the app into
 *    "offline" and records how old the oldest figure on screen is; the banner
 *    says "Offline — figures as of 14:32". The first fresh answer clears it.
 *  * **Reads only.** A write asks the server to decide something (mark paid, a
 *    category, a review decision). With no connection it is refused up front —
 *    "Needs a connection — nothing was changed" — never queued.
 *  * **Only the read screens' first answers**: home, cards, cycles, cashback,
 *    the first page of transactions, statements, rewards — what someone checks
 *    at a shop counter. Not every query a session ever made.
 *  * **Forgotten on sign-out**, and when the server refuses the session.
 */

import { clientType } from "@/platform";
import { setMonthEndWidget } from "@/platform/widget";

const PREFIX = "mydeck.offline:";

/** Paths (without query string) whose GET answers are kept. */
const KEPT = [
  /^\/api\/auth\/status$/,
  /^\/api\/info$/,
  /^\/api\/dashboard$/,
  /^\/api\/cards(\/[^/]+)?$/,
  /^\/api\/cycles$/,
  /^\/api\/cashback(\/.*)?$/,
  /^\/api\/statements$/,
  /^\/api\/statements\/review-queue$/,
  /^\/api\/caps\/(cards|cards\/[^/]+)$/,
  /^\/api\/transactions$/,
  /^\/api\/transactions\/categories$/,
  /^\/api\/sync\/status$/,
];

/** Whether this client keeps last-good answers at all. */
export function offlineEnabled(): boolean {
  return clientType() !== "web";
}

/** Whether a GET to `path` is one whose answer is kept. Transactions only for
 *  the first page — a saved page 40 is not what anyone needs offline. */
export function kept(path: string): boolean {
  const [base = "", query = ""] = path.split("?");
  if (!KEPT.some((re) => re.test(base))) return false;
  if (base === "/api/transactions") {
    const offset = new URLSearchParams(query).get("offset");
    return !offset || offset === "0";
  }
  return true;
}

type Saved = { at: number; data: unknown };

export function save(path: string, data: unknown, now = Date.now()): void {
  if (!offlineEnabled() || !kept(path)) return;
  try {
    localStorage.setItem(PREFIX + path, JSON.stringify({ at: now, data } satisfies Saved));
  } catch {
    /* storage full or refused: the screen just has no offline copy */
  }
}

export function load(path: string): Saved | null {
  if (!offlineEnabled()) return null;
  try {
    const raw = localStorage.getItem(PREFIX + path);
    return raw ? (JSON.parse(raw) as Saved) : null;
  } catch {
    return null;
  }
}

/** Drop every saved answer — on sign-out, or a session the server refused —
 *  and blank the home-screen widget, which holds figures too (M7). */
export function forgetAll(): void {
  void setMonthEndWidget(null);
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    /* nothing stored, or storage refused */
  }
}

/* -------------------------------------------------------------- offline state */

export type OfflineState = { offline: boolean; asOf: number | null };

let state: OfflineState = { offline: false, asOf: null };
const listeners = new Set<() => void>();

function set(next: OfflineState): void {
  if (next.offline === state.offline && next.asOf === state.asOf) return;
  state = next;
  listeners.forEach((l) => l());
}

export function getOfflineState(): OfflineState {
  return state;
}

export function subscribeOffline(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A saved answer from `at` is on screen: offline, as of the oldest figure shown. */
export function servedSaved(at: number): void {
  set({ offline: true, asOf: state.offline && state.asOf !== null ? Math.min(state.asOf, at) : at });
}

/** A fresh answer arrived: back online. */
export function servedFresh(): void {
  set({ offline: false, asOf: null });
}

/** The words a refused write shows. */
export const NEEDS_CONNECTION = "Needs a connection — nothing was changed.";
